import { useCallback, useEffect, useRef, useState } from 'react'
import type { Selection } from '../urlState'
import { canSplit, foldToOne, loadPanes, savePanes, type Pane } from './panes'
import { isPaneFrame } from './paneMode'
import { setRelayTarget } from '../transport/relay'

/**
 * SPLIT SCREEN — the panes, the drag that creates them, the files dock that rides
 * them, and the two transitions that keep the feature from being a trap. Moved out of
 * App.tsx in M7 (2026-09-03), verbatim: every comment below predates the move and is
 * the design record, not narration.
 *
 * What deliberately did NOT move:
 *   · `winW` and the resize listener — the listener's own comment refuses a second
 *     subscriber ("two listeners on one resize is two chances to disagree about the
 *     width"), so the hook exposes `foldIfNarrow(width)` for App's single listener to
 *     call instead of listening again.
 *   · the url.sel → relay effect belongs to the SHELL (App.tsx), not here — it follows
 *     ANY selection (deep link, reload, back/forward, and every pane iframe), so it must
 *     not be gated by split. NOTE (2026-09-03): the first version of this split DELETED
 *     that effect while claiming it stayed — the regression behind the owner's two
 *     split-screen bugs. It is restored in App.tsx; this comment is the reminder that
 *     it is the shell's, and that its absence is invisible until a real two-pane drag.
 */
export function useSplitScreen(args: {
  paneFrame: boolean
  urlSel: Selection
  writeUrl: (patch: { sel: Selection; view: null }, mode: 'replace') => void
}) {
  const { paneFrame, urlSel, writeUrl } = args
  const [panes, setPanes] = useState<Pane[]>(() => (isPaneFrame() ? [] : loadPanes()))
  const [activePane, setActivePane] = useState<string | null>(null)
  /* Read from the resize listener, which is bound once and cannot see re-renders. */
  const activePaneRef = useRef<string | null>(null)
  activePaneRef.current = activePane
  /** True while a conversation is being dragged out of the rail — see PaneStrip. */
  const [railDrag, setRailDrag] = useState(false)
  /** Which pane the Files dock is showing, or null when it is closed. */
  const [dockPaneId, setDockPaneId] = useState<string | null>(null)
  /** A permanently-empty socket ref: the dock's browser has no terminal, by design. */
  /** What is being dragged. A ref: the drop handler runs outside the drag's render. */
  const railDragSel = useRef<Selection>(null)
  /*
   * THE STRIP IS UP WHILE A DRAG IS IN THE AIR, even at one pane — otherwise there is
   * nothing to drop ONTO, and the first split could never be made. Chicken and egg:
   * the target has to exist before the drop that creates the second pane.
   */
  const split = !paneFrame && (panes.length > 1 || (railDrag && panes.length >= 1))
  /*
   * The dock follows FOCUS, not the pane it was opened from: a person who opens the
   * files, then clicks into another conversation, is looking at that one. It closes
   * itself when the pane it was showing goes away.
   */
  const dockPane = dockPaneId ? (panes.find((p) => p.id === activePane) ?? panes.find((p) => p.id === dockPaneId)) : null
  useEffect(() => {
    if (dockPaneId && !panes.some((p) => p.id === dockPaneId)) setDockPaneId(null)
  }, [dockPaneId, panes])
  /*
   * POINT THE RELAY AT THE MACHINE THE DOCK IS BROWSING. Its queries go through the
   * ambient relay target, and while split the shell has no conversation of its own to
   * point it at — so the dock's machine is the only thing it should be addressing.
   */
  useEffect(() => {
    const dev = dockPane?.sel?.kind === 'agent' ? (dockPane.sel.device ?? null) : null
    if (dev) setRelayTarget(dev)
  }, [dockPane])
  useEffect(() => {
    // The shell's own stage and composer belong to no conversation while split; the
    // stylesheet takes them out rather than each of them learning about panes.
    document.body.classList.toggle('split', split)
  }, [split])
  useEffect(() => {
    // One width for the dock's edge: the stylesheet gives it to #paneDock AND to the
    // file viewer that stops beside it, so they cannot disagree.
    document.body.classList.toggle('dock-open', !!dockPaneId)
  }, [dockPaneId])

  /*
   * The layout is the device's, not the URL's — a link is about a conversation, and
   * three panes in one would open somebody else's workspace on a screen that may not
   * fit it.
   */
  useEffect(() => {
    savePanes(panes)
  }, [panes])

  /*
   * THE TWO TRANSITIONS, and without them this is a trap rather than a feature.
   *
   * Splitting CLEARS the shell's own selection: otherwise the parent keeps its stage
   * mounted behind the strip — a whole conversation polling a machine, invisible, with
   * a terminal socket open. That is the same thing `foldToOne` closes panes to avoid,
   * reached from the other direction.
   *
   * Coming back to ONE pane hands that conversation back to the shell, so closing a
   * split leaves the app exactly as it would have been without one — not as a
   * single-pane strip that merely looks like it.
   */
  useEffect(() => {
    if (paneFrame) return
    if (panes.length > 1 && urlSel) writeUrl({ sel: null, view: null }, 'replace')
    /*
     * `!railDrag` matters: a drag SEEDS a single pane out of what is on screen so the
     * drop has a target, and folding that back the instant it appears would undo the
     * setup before the pointer had moved. One pane is only "not split" once the drag is
     * over and it is still one.
     */
    else if (panes.length === 1 && !railDrag) {
      const only = panes[0]!
      setPanes([])
      writeUrl({ sel: only.sel, view: null }, 'replace')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panes.length, paneFrame, railDrag])

  /** Called by App's ONE resize listener — see the header for why this is not a
   *  second subscriber. useCallback([]) is LOAD-BEARING: the listener mounts once and
   *  closes over this, so it must be the same function on every render — everything
   *  inside is stable (a setState, a ref, an import), which is what makes [] honest. */
  const foldIfNarrow = useCallback((width: number) => {
    if (!canSplit(width)) setPanes((ps) => foldToOne(ps, activePaneRef.current))
  }, [])

  return {
    panes, setPanes,
    activePane, setActivePane, activePaneRef,
    railDrag, setRailDrag, railDragSel,
    dockPaneId, setDockPaneId, dockPane,
    split, foldIfNarrow,
  }
}
